/**
 * AppProviders — single source of truth for the console's React providers.
 *
 * AuthProvider (Google sign-in) gates access to the console. WalletProvider
 * wraps everything so the post-login surfaces (Analytics, Vault Control) can
 * connect a wallet for on-chain reads/writes. WalletModal + AlertPopup are
 * rendered once at the root.
 */

import type { ReactNode } from "react";
import { BrowserRouter } from "react-router-dom";
import { WalletProvider, WalletModal, AlertPopup } from "@thema/ui/wallet";
import { DataApiProvider } from "@thema/ui";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { DATA_API_URL } from "./auth-config";
import App from "./App";

const WALLET_CONNECT_PROJECT_ID =
  import.meta.env.VITE_WALLET_CONNECT_PROJECT_ID || "";

/**
 * Supplies the data-API base URL + the signed-in user's Google ID token to the
 * data hooks. Lives INSIDE AuthProvider so `authToken` tracks the session; the
 * per-wallet analytics hooks send it as a bearer credential.
 */
function DataApiBridge({ children }: { children: ReactNode }) {
  const { idToken } = useAuth();
  return (
    <DataApiProvider baseUrl={DATA_API_URL} authToken={idToken}>
      {children}
    </DataApiProvider>
  );
}

export function AppProviders() {
  return (
    <BrowserRouter>
      <WalletProvider
        projectId={WALLET_CONNECT_PROJECT_ID}
        appMetadata={{
          name: "Thema Console",
          description: "Operate Thema vaults — analytics and capital flows.",
          url: typeof window !== "undefined" ? window.location.origin : "",
          icons: ["/favicon.png"],
        }}
      >
        <AuthProvider>
          <DataApiBridge>
            <App />
            <WalletModal />
            <AlertPopup position="top-right" />
          </DataApiBridge>
        </AuthProvider>
      </WalletProvider>
    </BrowserRouter>
  );
}
